"use client"

import type {
    DesktopViewMode,
    MobileViewMode
} from "@/app/(site)/contexts/product-grid/ProductGridContext"
import {
    useFilterPanelContext,
    useStaticGridContext
} from "@/app/(site)/contexts/product-grid/ProductGridContext"
import { FOCUS_VISIBLE_CLASS } from "@/src/utils/a11y/focusVisible"
import { clsx } from "@/src/utils/clsx"
import { useLangCode } from "@/src/utils/locale/useLangCode"
import { t } from "@/translations"
import { useState } from "react"

type ViewToggleModes = DesktopViewMode | MobileViewMode

type ViewToggleProps<T extends ViewToggleModes> = {
    nHits: number
    modes: T[]
    defaultMode: T
    onModeChange: (mode: T) => void
    adjustForFilterPanel?: boolean
    className?: string
    style?: React.CSSProperties
    disabled?: boolean
}

export function ViewToggle<T extends ViewToggleModes>({
    nHits,
    modes,
    defaultMode,
    onModeChange,
    adjustForFilterPanel = false,
    className,
    style,
    disabled = false
}: ViewToggleProps<T>) {
    const langCode = useLangCode()
    const [activeMode, setActiveMode] = useState(defaultMode)
    const { isFilterPanelOpen } = useFilterPanelContext()
    const { categoryTranslations } = useStaticGridContext()

    const handleModeChange = (mode: T) => {
        setActiveMode(mode)
        onModeChange(mode)
    }

    return (
        <div
            className={clsx("flex items-center bg-col-bg-primary", className)}
            style={style}
        >
            <span className="pt-2 Trade_Gothic_Text_12_150_0_300">
                {nHits} {categoryTranslations.products}
            </span>
            <div className="flex items-center gap-8">
                <span className="pt-2 Trim_Mono_POC_Text_14_130_0_400_uppercase">
                    {categoryTranslations.view}
                </span>
                <div className="flex gap-4">
                    {modes.map((mode, index) => (
                        <div key={`${mode}-${index}`} className="relative">
                            <button
                                type="button"
                                className={clsx(
                                    "flex h-24 items-center justify-center border border-col-border-neutral px-12 transition-colors duration-150",
                                    FOCUS_VISIBLE_CLASS,
                                    activeMode === mode && "bg-col-bg-calm",
                                    disabled && "opacity-50"
                                )}
                                onClick={
                                    !disabled && activeMode !== mode
                                        ? () => handleModeChange(mode)
                                        : undefined
                                }
                                disabled={disabled}
                                aria-label={t(langCode, "category_page.column_view", {
                                    mode
                                })}
                                aria-pressed={activeMode === mode}
                            >
                                <span className="Trim_Mono_POC_Text_12_130_-3_400_uppercase lg:pt-1">
                                    {adjustForFilterPanel && isFilterPanelOpen
                                        ? mode - 1
                                        : mode}
                                </span>
                            </button>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    )
}
